import dayjs from 'dayjs';
import type { BadgeCounts } from '../hooks/useBadges';

/**
 * WHAT EVERY NAV BADGE IS MADE OF, AND WHERE ITS ROWS ARE.
 *
 * A red pill in the navbar is a promise: there is work here, and opening this takes you to it. Three
 * questions decide whether it keeps that promise, and until this file existed the nav could answer
 * none of them:
 *
 *   1. **Where did this number come from?** Half the nav badges are SUMS of unrelated queues, added
 *      up in the menu and never named — Attendance was `anomalies + regularizations + incomplete`,
 *      Master Stock four separate queues, Field Staff Advances two. A reader saw "4" and had no way
 *      to learn it was "2 doubtful punches, 1 missing punch, 1 fix request".
 *   2. **Why is it not on the screen?** Several badges count a slice that the page's DEFAULT view
 *      does not show — a period that ends before the backlog starts, a status the register filters
 *      out. The number named work the link did not open.
 *   3. **How do I clear it?** Answered only by landing on the rows themselves.
 *
 * So each badge is declared here as a list of PARTS, and the pill's number is the sum of exactly
 * these parts — never a separate expression that happens to agree. Add a part, and the total moves
 * with it; that is what makes the breakdown a partition rather than a commentary
 * (`summary-chips-add-up`). Each part carries:
 *
 *   • `n`     — how many, straight off `/badges`, so tooltip and pill cannot disagree;
 *   • `label` — what those rows ARE, already pluralised, in the reader's words, not the column's;
 *   • `to`    — the slice that shows EXACTLY those rows. Not the page that contains them: a link to
 *               a register whose default view filters the rows out is the bug this file exists for.
 *
 * The links deliberately mirror the ones `/worklist` already hands My Work
 * (`backend/src/modules/worklist/worklist.module.ts`, section 4) — that module and this one are the
 * two places the same queues are turned into addresses, and they must stay the same addresses.
 */
export interface BadgePart {
  /** How many rows. Parts at zero are dropped, so a breakdown never lists work that isn't there. */
  n: number;
  /** Already pluralised for `n`, and phrased as the thing itself: "orders waiting for your yes or no". */
  label: string;
  /** The slice holding exactly these rows — a filtered register, not its bare front page. */
  to: string;
  /**
   * Yours, but sitting with somebody else — you cannot clear it by acting.
   *
   * Counting un-clearable work in a badge is how a number comes to sit there forever
   * (`badge-counts-one-step`), so where the nav does carry one it has to SAY so rather than let the
   * reader hunt for a button that does not exist.
   */
  waiting?: boolean;
}

/** Who the reader is — the few badges whose meaning changes with the role ask these. */
export interface BadgeCtx {
  isWarehouseManager: boolean;
  /** `hasAnyPermission` from the auth store. */
  can: (perms: string[]) => boolean;
}

/** Drops empty parts at the point of declaration, so every list below reads as just its contents. */
const part = (n: number | undefined, label: string, to: string, waiting = false): BadgePart | null =>
  n && n > 0 ? { n, label, to, ...(waiting ? { waiting } : {}) } : null;

/** `plural(1, 'order', 'orders')` → 'order'. Written out because "1 orders" is how a badge loses trust. */
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

const compact = (parts: (BadgePart | null)[]): BadgePart[] => parts.filter((p): p is BadgePart => p != null);

/** The sum a pill shows. Always derived from the parts, never written out a second time. */
export const badgeTotal = (parts: BadgePart[] | undefined): number =>
  (parts ?? []).reduce((s, p) => s + p.n, 0);

/**
 * The attendance review slice, over the WHOLE backlog.
 *
 * The register opens on the last seven days while the two review counters run to the beginning of
 * time, so `/ffa/attendance` on its own drops the reviewer on a week that need not hold a single one
 * of the days the badge just counted — the exact "badge says 1, table says No data" the nav was
 * reported for. `attendanceReviewSince` is the oldest day still outstanding, so the period is
 * widened to cover precisely what was counted.
 */
export const attendanceReviewLink = (b: BadgeCounts): string => {
  const since = b.attendanceReviewSince;
  return `/ffa/attendance?tab=log&view=list&review=1&active=0${since ? `&range=${since}~${dayjs().format('YYYY-MM-DD')}` : ''}`;
};

/**
 * The returns queue in the READER's lane.
 *
 * `returnsActionable` is already role-shaped server-side (an approver counts returns awaiting
 * approval, a warehouse manager ones to take in, Accounts ones awaiting a credit note), so the slice
 * has to be role-shaped the same way or the link opens somebody else's statuses. Mirrors
 * `BadgesService.returnsBadge`.
 */
const returnsLane = (ctx: BadgeCtx): { statuses: string; label: (n: number) => string } => {
  if (ctx.can(['RETURN.APPROVE'])) {
    return { statuses: 'REQUESTED', label: (n) => `goods ${plural(n, 'return', 'returns')} waiting for your decision` };
  }
  if (ctx.isWarehouseManager) {
    return { statuses: 'APPROVED,IN_TRANSIT', label: (n) => `goods ${plural(n, 'return', 'returns')} to take in at your godown` };
  }
  if (ctx.can(['CREDIT_NOTE.CREATE'])) {
    return { statuses: 'RECEIVED', label: (n) => `received ${plural(n, 'return', 'returns')} still owed a credit note` };
  }
  return { statuses: '', label: (n) => `goods ${plural(n, 'return', 'returns')} on the move` };
};

/**
 * Every badged nav entry, keyed by its nav `key`, as the parts that make up its number.
 *
 * Called once per render with the live `/badges` payload; entries whose parts are all zero simply
 * produce an empty list, and the menu renders no pill at all.
 */
export function navBadgeParts(b: BadgeCounts, ctx: BadgeCtx): Record<string, BadgePart[]> {
  const attReview = attendanceReviewLink(b);
  const ret = returnsLane(ctx);

  /*
   * Orders means two different jobs. Somebody who bills orders but does not fulfil them (Accounts)
   * has exactly ONE step on that register — uploading the Tally invoice for packed goods — so their
   * badge is that count and its slice is the register's own "To invoice" lane. Everyone else gets
   * the orders still moving. Same branch the menu used to make inline; kept here so the number, its
   * words and its link are decided in one place.
   */
  const ordersIsInvoiceUpload = b.invoicesToUpload !== undefined && !ctx.can(['ORDER.EDIT']);
  const orders = ordersIsInvoiceUpload
    ? compact([
        part(b.invoicesToUpload, `Tally ${plural(b.invoicesToUpload ?? 0, 'invoice', 'invoices')} to upload before the goods can leave`,
          '/orders?status=PACKED&lane=to-invoice'),
      ])
    : compact([
        part(b.ordersActive, ctx.isWarehouseManager
          ? `${plural(b.ordersActive ?? 0, 'order', 'orders')} at your godown, still to pack and send`
          : `${plural(b.ordersActive ?? 0, 'order', 'orders')} still being worked on`,
          '/orders'),
      ]);

  return {
    // ── Warehouse ────────────────────────────────────────────────────────────────────────────────
    '/warehouse/dashboard': compact([
      part(b.ordersActive, `${plural(b.ordersActive ?? 0, 'order', 'orders')} to pack and send`, '/orders'),
      part(b.transfersAwaitingDispatch, `stock ${plural(b.transfersAwaitingDispatch ?? 0, 'transfer', 'transfers')} to send out`, '/inventory/master-stock?tab=transfers'),
      part(b.transfersAwaitingReceipt, `stock ${plural(b.transfersAwaitingReceipt ?? 0, 'transfer', 'transfers')} to receive`, '/inventory/master-stock?tab=transfers'),
      part(b.transfersAwaitingResolution, `short ${plural(b.transfersAwaitingResolution ?? 0, 'delivery', 'deliveries')} to settle`, '/inventory/master-stock?tab=transfers'),
    ]),
    '/inventory/master-stock': compact([
      part(b.transfersAwaitingDispatch, `stock ${plural(b.transfersAwaitingDispatch ?? 0, 'transfer', 'transfers')} to send out`, '/inventory/master-stock?tab=transfers'),
      part(b.transfersAwaitingReceipt, `stock ${plural(b.transfersAwaitingReceipt ?? 0, 'transfer', 'transfers')} to receive`, '/inventory/master-stock?tab=transfers'),
      part(b.transfersAwaitingResolution, `short ${plural(b.transfersAwaitingResolution ?? 0, 'delivery', 'deliveries')} to settle`, '/inventory/master-stock?tab=transfers'),
      part(b.adjustmentsPendingApproval, `stock ${plural(b.adjustmentsPendingApproval ?? 0, 'correction', 'corrections')} to approve`, '/inventory/master-stock?tab=adjustments'),
    ]),

    // ── Masters ──────────────────────────────────────────────────────────────────────────────────
    // Both parts live on the Coverage tab; the bare page opens on Structure, which shows neither.
    '/masters/territories': compact([
      /*
       * "Places", not "beats". This counter was widened to every unattended NODE — a state, a
       * district or a beat, rolled up to the highest point where the gap starts — so the old word
       * named a third of what the number counted. Same wording `/worklist` uses for the same count.
       */
      part(b.territoriesUnassigned, `${plural(b.territoriesUnassigned ?? 0, 'place', 'places')} with no sales person`, '/masters/territories?tab=coverage'),
      part(b.coverageNeeded, `sales ${plural(b.coverageNeeded ?? 0, 'person', 'people')} off today with nobody covering`, '/masters/territories?tab=coverage'),
    ]),

    // ── People ───────────────────────────────────────────────────────────────────────────────────
    // Farmer self-onboarding — mirrors worklist.module.ts (farmers.verify / farmers.signups).
    '/people': compact([
      part(b.farmersToVerify, `${plural(b.farmersToVerify ?? 0, 'farmer', 'farmers')} who signed up in the app, to verify`, '/people?tab=verify'),
      part(b.farmerSignupsToCall, `${plural(b.farmerSignupsToCall ?? 0, 'person', 'people')} who did not finish signing up, to call`, '/people?tab=signups&status=OPEN&toCall=1'),
    ]),

    // ── Sales ────────────────────────────────────────────────────────────────────────────────────
    '/orders': orders,
    '/orders/approval': compact([
      part(b.ordersPendingApproval, `${plural(b.ordersPendingApproval ?? 0, 'order', 'orders')} waiting for your yes or no`, '/orders/approval'),
    ]),
    '/orders/shortfalls': compact([
      part(b.shortfallsOpen, `${plural(b.shortfallsOpen ?? 0, 'order', 'orders')} short of stock, waiting on a decision`, '/orders/shortfalls'),
    ]),
    '/inquiries': compact([
      part(b.openInquiries, `${plural(b.openInquiries ?? 0, 'enquiry', 'enquiries')} still open`, '/inquiries?status=NEW,ASSIGNED,CONTACTED'),
    ]),

    // ── Billing ──────────────────────────────────────────────────────────────────────────────────
    '/billing/invoices': compact([
      part(b.openInvoices, `${plural(b.openInvoices ?? 0, 'bill', 'bills')} not yet fully paid`, '/billing/invoices?balance=OUTSTANDING'),
    ]),
    '/returns': compact([part(b.returnsActionable, ret.label(b.returnsActionable ?? 0), ret.statuses ? `/returns?status=${ret.statuses}` : '/returns')]),
    '/billing/payments': compact([
      part(b.paymentsPending, `${plural(b.paymentsPending ?? 0, 'payment', 'payments')} waiting to be checked`, '/billing/payments?status=PENDING'),
    ]),

    // ── Field Force ──────────────────────────────────────────────────────────────────────────────
    /*
     * Three separate queues were being added up and shown as one number with no name on any part of
     * it. They are also cleared in three different places — two on the log tab (one reviewer act
     * each), one on the Regularization tab (an approval) — so a single link could never have opened
     * all of it. Each part now opens its own.
     */
    '/ffa/attendance': compact([
      part(b.attendanceAnomalies, `doubtful attendance ${plural(b.attendanceAnomalies ?? 0, 'day', 'days')} to check`, attReview),
      part(b.attendanceIncomplete, `attendance ${plural(b.attendanceIncomplete ?? 0, 'day', 'days')} missing a punch`, attReview),
      part(b.regularizationsPending, `attendance fix ${plural(b.regularizationsPending ?? 0, 'request', 'requests')} to decide`, '/ffa/attendance?tab=reg'),
    ]),
    '/events': compact([
      part(b.upcomingEvents, `${plural(b.upcomingEvents ?? 0, 'meeting', 'meetings')} coming up`, `/events?status=PLANNED&range=${dayjs().format('YYYY-MM-DD')}~`, true),
    ]),
    /*
     * The wallet badge adds a decision you owe somebody to a request of your own that is sitting with
     * the office. The second half is not yours to act on at all, so it is marked `waiting` and the
     * breakdown says who has it — otherwise the reader goes looking for a button that is not there.
     */
    '/wallets': compact([
      part(b.advanceRequestsAwaitingMe, `money ${plural(b.advanceRequestsAwaitingMe ?? 0, 'request', 'requests')} from staff to decide`, '/wallets?view=requests'),
      part(b.myAdvanceRequestsPending, `of your own money ${plural(b.myAdvanceRequestsPending ?? 0, 'request is', 'requests are')} with the office`, '/wallets', true),
    ]),
    '/expense-reports': compact([
      part(b.expenseAwaitingMe, `expense ${plural(b.expenseAwaitingMe ?? 0, 'claim', 'claims')} to check`, '/expense-reports?status=SUBMITTED'),
    ]),

    // ── Cross-cutting ────────────────────────────────────────────────────────────────────────────
    '/approvals': compact([
      part(b.approvalsWaiting, `${plural(b.approvalsWaiting ?? 0, 'decision', 'decisions')} waiting on you`, '/approvals'),
      part(b.approvalsMine, `of your own ${plural(b.approvalsMine ?? 0, 'request was', 'requests were')} sent back for you to fix`, '/approvals?tab=mine'),
    ]),
    '/complaints': compact([
      part(b.openComplaints, `${plural(b.openComplaints ?? 0, 'complaint', 'complaints')} still open`, '/complaints'),
    ]),
  };
}
